import React from 'react'

interface ZoneFilterProps {
  selectedZone: string
  onZoneChange: (zone: string) => void
}

// USDA Hardiness Zones - 2023 Official Data
const zones = [
  { zone: '1a', minTemp: '-60°F', maxTemp: '-55°F', description: 'Alaska' },
  { zone: '1b', minTemp: '-55°F', maxTemp: '-50°F', description: 'Alaska' },
  { zone: '2a', minTemp: '-50°F', maxTemp: '-45°F', description: 'Alaska, northern Canada' },
  { zone: '2b', minTemp: '-45°F', maxTemp: '-40°F', description: 'Alaska, northern Canada' },
  { zone: '3a', minTemp: '-40°F', maxTemp: '-35°F', description: 'Northern Montana, North Dakota' },
  { zone: '3b', minTemp: '-35°F', maxTemp: '-30°F', description: 'Northern Maine, Wisconsin' },
  { zone: '4a', minTemp: '-30°F', maxTemp: '-25°F', description: 'Northern New York, Vermont' },
  { zone: '4b', minTemp: '-25°F', maxTemp: '-20°F', description: 'Iowa, Nebraska, Wyoming' },
  { zone: '5a', minTemp: '-20°F', maxTemp: '-15°F', description: 'Colorado, Illinois, Massachusetts' },
  { zone: '5b', minTemp: '-15°F', maxTemp: '-10°F', description: 'Connecticut, Missouri, Pennsylvania' },
  { zone: '6a', minTemp: '-10°F', maxTemp: '-5°F', description: 'New Jersey, Oregon, Washington' },
  { zone: '6b', minTemp: '-5°F', maxTemp: '0°F', description: 'Arkansas, Kentucky, Virginia' },
  { zone: '7a', minTemp: '0°F', maxTemp: '5°F', description: 'Maryland, North Carolina, Tennessee' },
  { zone: '7b', minTemp: '5°F', maxTemp: '10°F', description: 'Delaware, Georgia, Oklahoma' },
  { zone: '8a', minTemp: '10°F', maxTemp: '15°F', description: 'Alabama, Louisiana, Mississippi' },
  { zone: '8b', minTemp: '15°F', maxTemp: '20°F', description: 'Florida, Texas, coastal areas' },
  { zone: '9a', minTemp: '20°F', maxTemp: '25°F', description: 'Arizona, California, Florida' },
  { zone: '9b', minTemp: '25°F', maxTemp: '30°F', description: 'Southern California, Florida, Hawaii' },
  { zone: '10a', minTemp: '30°F', maxTemp: '35°F', description: 'Southern Florida, Hawaii' },
  { zone: '10b', minTemp: '35°F', maxTemp: '40°F', description: 'Hawaii, southern Florida' },
  { zone: '11a', minTemp: '40°F', maxTemp: '45°F', description: 'Hawaii, Puerto Rico' },
  { zone: '11b', minTemp: '45°F', maxTemp: '50°F', description: 'Hawaii, Puerto Rico' },
  { zone: '12a', minTemp: '50°F', maxTemp: '55°F', description: 'Puerto Rico, USVI' },
  { zone: '12b', minTemp: '55°F', maxTemp: '60°F', description: 'Puerto Rico, USVI' },
  { zone: '13a', minTemp: '60°F', maxTemp: '65°F', description: 'Puerto Rico, USVI' },
  { zone: '13b', minTemp: '65°F', maxTemp: '70°F', description: 'Puerto Rico, USVI' },
]

const ZoneFilter: React.FC<ZoneFilterProps> = ({ selectedZone, onZoneChange }) => {
  const selectedZoneData = zones.find(z => z.zone === selectedZone)

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h4>🗺️ Hardiness Zone Filter</h4>
      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
        Select your USDA zone to show only plants that will survive in your area.
        <a 
          href="https://planthardiness.ars.usda.gov/" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#2d5a27', marginLeft: '0.5rem' }}
        >
          Find your zone on the official USDA map →
        </a>
      </p>
      
      <div style={{ marginTop: '0.75rem' }}>
        <select
          value={selectedZone}
          onChange={(e) => onZoneChange(e.target.value)}
          style={{ width: '100%', padding: '0.5rem' }}
        >
          <option value="">-- All Zones (Show All Plants) --</option>
          {zones.map(z => (
            <option key={z.zone} value={z.zone}>
              Zone {z.zone} ({z.minTemp} to {z.maxTemp}) - {z.description}
            </option>
          ))}
        </select>
      </div>

      {selectedZone && selectedZoneData && (
        <div style={{ 
          marginTop: '0.75rem', 
          padding: '0.75rem', 
          background: '#e3f2fd', 
          borderRadius: '8px',
          fontSize: '0.85rem'
        }}>
          <strong>Zone {selectedZone}:</strong> {selectedZoneData.minTemp} to {selectedZoneData.maxTemp}
          <br />
          <span style={{ color: '#666' }}>{selectedZoneData.description}</span>
          <br />
          <span style={{ color: '#2d5a27', marginTop: '0.5rem', display: 'block' }}>
            ✓ Showing only plants hardy to Zone {selectedZone} or lower
          </span>
        </div>
      )}
    </div>
  )
}

// Helper function to check if a plant can grow in a given zone
export const plantSurvivesInZone = (plantZones: string, selectedZone: string): boolean => {
  if (!selectedZone) return true // No zone selected, show all
  if (!plantZones) return true // Plant has no zone data, show it
  
  // Parse plant zone range (e.g., "4-9", "7-10", "3-11")
  const plantMatch = plantZones.match(/(\d+)([ab])?\s*-\s*(\d+)([ab])?/)
  if (!plantMatch) {
    // Try single zone (e.g., "9", "10b")
    const singleMatch = plantZones.match(/(\d+)([ab])?/)
    if (!singleMatch) return true // Can't parse, show it
    
    const plantZone = parseInt(singleMatch[1])
    const selectedZoneNum = parseInt(selectedZone)
    
    return selectedZoneNum >= plantZone
  }
  
  const plantMinZone = parseInt(plantMatch[1])
  const plantMaxZone = parseInt(plantMatch[3])
  const selectedZoneNum = parseInt(selectedZone)
  
  // Check if selected zone is within plant's range
  // Plant can survive in zones from min to max
  // So if user selects zone 7, plant with range 4-9 will survive
  return selectedZoneNum >= plantMinZone && selectedZoneNum <= plantMaxZone
}

export default ZoneFilter
